/**
 * Смена ленты. Отдельные настройки открываются в новом документе.
 *
 * Адрес меняется через history, чтобы обновить вкладку можно было и чтобы
 * «назад» вернуло предыдущую ленту. Сокет переоткрывается только когда
 * меняется источник: лайв, прематч и лента 5.0 — один и тот же кадр.
 */

import { create } from 'zustand';

import { adoptFeed, PRIVATE_MIRROR, FEED_KIND, feedKindOf, type FeedKind } from '../lib/feedContext';
import { retargetLiveFeed, syncAllFeed } from './liveFeed';
import { isAllFeed, pageLine } from './route';
import { useScreen } from './store';
import { preferenceScope } from '../lib/feedPreferences';
import type { FeedId } from './PilotHead';

/** Абсолютные адреса. Префикс сборки (/tests) сюда не подмешивается: иначе клик остаётся внутри ленты 2.0. */
const HREF: Record<FeedId, string> = {
  all: '/app/all',
  prematch: '/app/prematch',
  live: '/app/live',
  lenta2: '/tests/live',
  lenta3: '/feed3/live',
  lenta5: '/app/live?tapes=5',
  lenta6: '/feed6',
  lenta7: '/feed7/live',
  russkie: '/russkie',
  'russkie-prematch': '/russkie/prematch',
  'russkie-live': '/russkie/live',
  transfers: '/transfers',
};

export function feedHref(id: FeedId): string {
  if (PRIVATE_MIRROR && (id === 'live' || id === 'prematch')) return `/cupis/${id}`;
  return HREF[id];
}

function kindOf(id: FeedId): FeedKind {
  if (id === 'lenta7') return 'feed7';
  if (id === 'lenta6') return 'feed6';
  if (id === 'lenta2') return 'tests';
  if (id === 'lenta3') return 'feed3';
  if (id === 'russkie' || id === 'russkie-live' || id === 'russkie-prematch') return 'ru';
  if (id === 'transfers') return 'transfers';
  return 'main';
}

/** Счётчик, чтобы лента перерисовалась после pushState: сам адрес React не будит. */
export const useFeedNav = create<{ n: number }>(() => ({ n: 0 }));

function bump(): void {
  useFeedNav.setState((s) => ({ n: s.n + 1 }));
}

export const FEED_SWITCH = 'forq-feed-switch';

/** Подтянуть метаданные уже выбранного источника. Слушает экран ленты. */
function askMeta(): void {
  window.dispatchEvent(new Event(FEED_SWITCH));
}

/**
 * Открыть ленту здесь же.
 * Лайв и прематч меняют только линию. Лента 5.0 — та же линия плюс поток «5».
 * 2.0 переоткрывает сокет; русские и переливные — документ.
 */
/** Scoped stores (money, profile, locks and popup state) are bound when the
 * document loads. Crossing that boundary must initialise its own stores;
 * merely changing the socket keeps the previous feed's bank and filters. */
export function needsFeedDocument(from: FeedKind, to: FeedKind): boolean {
  return preferenceScope(from) !== preferenceScope(to);
}

export function switchFeed(id: FeedId): void {
  if (PRIVATE_MIRROR) { location.assign(feedHref(id)); return; }
  const href = HREF[id];
  const kind = kindOf(id);
  const here = `${location.pathname}${location.search}`;
  const prev = FEED_KIND;
  if (needsFeedDocument(prev, kind)) { location.assign(href); return; }
  if (here === href && prev === kind && useScreen.getState().mode === 'feed') return;
  adoptFeed(kind);
  if (here !== href) history.pushState({ feed: id }, '', href);
  useScreen.getState().setMode('feed');
  useScreen.getState().setShelf('work');
  if (kind !== prev) {
    retargetLiveFeed();
    askMeta();
  }
  syncAllFeed();
  bump();
}

function syncFromLocation(): void {
  const kind = feedKindOf(location);
  const prev = FEED_KIND;
  if (needsFeedDocument(prev, kind)) { location.reload(); return; }
  adoptFeed(kind);
  const path = location.pathname.replace(/\/+$/, '');
  const dedicated =
    /\/(live|prematch)$/.test(path) ||
    path === '/tests' ||
    path.startsWith('/tests/') ||
    path.endsWith('/russkie') ||
    path.endsWith('/transfers') ||
    path === '/feed3' ||
    path.startsWith('/feed3/');
  if (dedicated || isAllFeed(path) || pageLine(path) !== null) useScreen.getState().setMode('feed');
  if (kind !== prev) {
    retargetLiveFeed();
    askMeta();
  }
  syncAllFeed();
  bump();
}

export function bindFeedHistory(): () => void {
  const onPop = (): void => syncFromLocation();
  window.addEventListener('popstate', onPop);
  return () => window.removeEventListener('popstate', onPop);
}
